import React, {Context, createContext, useContext, useLayoutEffect, useState} from "react";

export enum SCREEN {
    XS = 0,
    SM = 600,
    MD = 940,
    LG = 1200,
    XL = 1440,
}

export interface IWindowSize {
    width: number;
    height: number;
    isLargeScreen: boolean;
    isMediumScreen: boolean;
    isSmallScreen: boolean;
    getSize: () => number;

    up: (size: SCREEN) => boolean;
    down: (size: SCREEN) => boolean;
    over: (num: number) => boolean;
    under: (num: number) => boolean;
    getValueBySize: (values) => any;
}

export const getWindowDimensions = (): IWindowSize => {
    const width = window.innerWidth;
    const height = window.innerHeight;
    const isLargeScreen = width > SCREEN.MD;
    const isMediumScreen = width > SCREEN.SM && !isLargeScreen;
    const isSmallScreen = width <= SCREEN.SM;
    const getSize = () => {
        if(width < SCREEN.SM) return SCREEN.XS
        if(width < SCREEN.MD) return SCREEN.SM
        if(width < SCREEN.LG) return SCREEN.MD
        if(width < SCREEN.XL) return SCREEN.LG
        return SCREEN.XL;
    };

    return {
        width,
        height,
        isLargeScreen,
        isMediumScreen,
        isSmallScreen,
        up: (size: SCREEN) => width >= size,
        down: (size: SCREEN) => width <= size,
        over: (num: number) => width > num,
        under: (num: number) => width < num,
        getSize,
        getValueBySize: (values) => {
            return values[getSize()]
        },
    };
}

const WindowSizeContext: Context<any> = createContext([{}, Function]);

let timeoutId;
const WindowSizeProvider = ({children}) => {
    const [state, setState] = useState(getWindowDimensions());

    useLayoutEffect(() => {
        const checkAndSetWindowDimensions = () => {
            const newSize = getWindowDimensions();
            if(newSize.width !== state.width || newSize.height !== state.height){
                setState(newSize);
            }
        };
        const debouncedFn = () => {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(checkAndSetWindowDimensions, 500);
        };

        window.addEventListener('resize', debouncedFn);
        return () => window.removeEventListener('resize', debouncedFn);
    }, []);

    return <WindowSizeContext.Provider value={[state, setState]}>
        {children}
    </WindowSizeContext.Provider>
};

export const useWindowSize = () => useContext(WindowSizeContext)[0];

export default WindowSizeProvider;
